import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Translate } from 'react-localize-redux';
import css from './LoginCardsGroup.css';
import { WarningMessage } from '../../../components/widgets';
import { GET_PARTNER_ACCOUNTS, SAVE_PARTNER_ACCOUNT } from '../../queries';
import LoginCard from './LoginCard';

const parseAccountToServer = (accountsByLoginId, loginId, loginMap) => {
    const login = accountsByLoginId.get(loginId);
    const loginData = login?.loginData || [];
    const serverAccounts = login?.loginAccounts || [];
    const loginAccounts = Array.from(loginMap.entries()).map(([accountId, enabled]) => {
        const account = serverAccounts.find(a => a.accountId === accountId);
        return { accountId, accountName: account?.accountName, enabled };
    });
    return { loginId, loginData, loginAccounts };
};

export default function LoginCardsGroup({
    accounts,
    searchText,
    partnerId,
    triggerOAuthFlow,
    guideLink,
    icon,
    partnerDisplayName,
    onDoneReady,
    onClose,
    onDone,
}) {
    const [pendingChanges, setPendingChanges] = useState(new Map());
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const [savePartnerAccount] = useMutation(SAVE_PARTNER_ACCOUNT);

    const accountsByLoginId = useMemo(() => {
        const map = new Map();
        accounts.forEach(login => map.set(login.loginId, login));
        return map;
    }, [accounts]);

    const filteredLoginsAndAccounts = useMemo(() => {
        if (!searchText) return accounts;
        const lowerSearch = searchText.toLowerCase();
        return accounts
            .map(login => {
                const loginIdMatches = login.loginId?.toLowerCase().includes(lowerSearch);
                const matchingAccounts = (login.loginAccounts || []).filter(
                    a =>
                        a.accountName?.toLowerCase().includes(lowerSearch) ||
                        a.accountId?.toLowerCase().includes(lowerSearch)
                );
                if (loginIdMatches || matchingAccounts.length > 0) {
                    return {
                        ...login,
                        loginAccounts: loginIdMatches ? login.loginAccounts : matchingAccounts,
                    };
                }
                return null;
            })
            .filter(Boolean);
    }, [accounts, searchText]);

    const toggleAccount = useCallback((loginId, accountId, enabled, serverEnabled) => {
        setPendingChanges(prev => {
            const next = new Map(prev);
            const loginMap = new Map(next.get(loginId) || []);
            // the user toggled it back to its original server state
            if (enabled === serverEnabled) {
                loginMap.delete(accountId);
            } else {
                loginMap.set(accountId, enabled);
            }
            if (loginMap.size === 0) {
                next.delete(loginId);
            } else {
                next.set(loginId, loginMap);
            }
            return next;
        });
    }, []);

    const getEffectiveEnabled = useCallback(
        (loginId, accountId, serverEnabled) => {
            const loginMap = pendingChanges.get(loginId);
            return loginMap?.has(accountId) ? loginMap.get(accountId) : serverEnabled;
        },
        [pendingChanges]
    );

    const handleDone = useCallback(async () => {
        if (pendingChanges.size > 0) {
            setSaving(true);
            setSaveError(null);

            try {
                const results = await Promise.all(
                    Array.from(pendingChanges.entries()).map(([loginId, loginMap]) => {
                        const { loginData, loginAccounts } = parseAccountToServer(accountsByLoginId, loginId, loginMap);
                        return savePartnerAccount({
                            variables: { partnerId, loginId, loginData, loginAccounts },
                            refetchQueries: [{ query: GET_PARTNER_ACCOUNTS, variables: { partnerId } }],
                        });
                    })
                );

                const firstError = results.find(r => r.data?.savePartnerAccount?.error);
                if (firstError) {
                    setSaveError(firstError.data.savePartnerAccount.error);
                    setSaving(false);
                    return;
                }

                setPendingChanges(new Map());
            } catch (err) {
                setSaveError(err.message);
                setSaving(false);
                return;
            }

            setSaving(false);
        }
        (onDone || onClose)();
    }, [pendingChanges, accounts, partnerId, savePartnerAccount, onDone, onClose]);

    const isDoneDisabled = (onDone && accounts.length === 0) || saving;

    useEffect(() => {
        onDoneReady({ handleDone, disabled: isDoneDisabled, loading: saving });
    }, [handleDone, isDoneDisabled, saving, onDoneReady]);

    return (
        <>
            {saveError && <WarningMessage show type="error" message={saveError} showIcon />}
            {filteredLoginsAndAccounts.length === 0 ? (
                <div className={css.emptyState}>
                    <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.USER_CONFIGURATION_SHELF.NO_MATCHING_ACCOUNTS" />
                </div>
            ) : (
                filteredLoginsAndAccounts.map(login => (
                    <LoginCard
                        key={login.loginId}
                        login={login}
                        partnerIcon={icon}
                        partnerDisplayName={partnerDisplayName}
                        onReauthorize={triggerOAuthFlow}
                        onToggleAccount={toggleAccount}
                        getEffectiveEnabled={getEffectiveEnabled}
                        guideLink={guideLink}
                    />
                ))
            )}
        </>
    );
}

LoginCardsGroup.propTypes = {
    accounts: PropTypes.array.isRequired,
    searchText: PropTypes.string.isRequired,
    partnerId: PropTypes.number.isRequired,
    triggerOAuthFlow: PropTypes.func.isRequired,
    guideLink: PropTypes.string,
    icon: PropTypes.string.isRequired,
    partnerDisplayName: PropTypes.string.isRequired,
    onDoneReady: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onDone: PropTypes.func,
};
